/**
 * View model for the A/B results page (`SplitTestDetail`).
 *
 * Owns the data (experiment, live metrics, the opt-in `goals=all` requests),
 * the goal list (every goal with its confidence), the selected goal and its
 * verdict, the click set draft with its live preview, engagement, test
 * health, the header facts, setup rows and every action. Selecting a goal
 * saves it as the test's goal when the viewer can (one interaction); share
 * links and older backends only switch the view (`?goal=`).
 * Catches are toast-only: the mutation hooks own observability reporting
 * (CLAUDE.md "Observability").
 */
import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import {
  useExperiments,
  useExperimentMetrics,
  useExperimentGoalMetrics,
  useSaveResultsGoal,
  useStopExperiment,
  useResumeExperiment,
  useArchiveExperiment,
  useCallExperiment,
  useShipWinner,
  type Experiment,
  type ExperimentVariant,
} from "@/hooks/useExperiments"
import {
  clickElements,
  clickSetIncludes,
  clickSetParam,
  compareGoal,
  KEY_CLICKS,
  destinationInput,
  effectiveGoal,
  formatChange,
  goalCandidates,
  goalKey,
  goalName,
  launchGoalCopy,
  launchResultsGoal,
  parseResultsGoal,
  presetToCustom,
  resultsGoalBody,
  sameGoal,
  selectGoalMetric,
  selectedCandidate,
  supportsResultsGoal,
  topClicksFromEvents,
  type ClickSet,
  type ClickSetTarget,
  type GoalContext,
  type GoalMetrics,
  type ResultsGoal,
  type VariantSource,
} from "@/lib/experiments/goalMetric"
import { compareProportions, formatChance } from "@/lib/experiments/bayes"
import { assessHealth } from "@/lib/experiments/testHealth"
import { callDecision } from "@/lib/experiments/resultsStage"
import { detailReadOnly, experimentPropertyId, lifecycleErrorMessage, type LifecycleAction } from "@/lib/experiments/detailPermissions"
import { outcomeSettling } from "@/lib/experiments/outcomeLadder"
import { buildResultsCsv, downloadCsv } from "@/lib/experiments/resultsCsv"
import { TEST_TYPE_CONFIGS, estimateCountDispersion, isCountGoal, type TestType } from "@/lib/utils/abTestStatistics"
import { evaluateGoals, goalCallPlan, goalRows } from "@/lib/experiments/goalVerdict"
import { parseSlugId } from "@/lib/routing/slug-id"
import { getErrorBody, getHttpStatus } from "@/lib/observability/shared"
import { formatTestGoalLabel } from "@/lib/utils/testGoal"
import { countryCodes } from "@/lib/constants/countries"
import { elementName } from "@/components/heatmaps/heatmapInsights"
import { clickSetIsValid, type GoalPreview } from "@/components/experiments/results/ClickSetEditor"
import type { GoalRowSummary } from "@/lib/experiments/goalVerdict"
import type { ResultRow } from "@/components/experiments/results/ResultRows"
import type { CallStatus } from "@/components/experiments/results/GoalDetailCard"
import type { SetupRow } from "@/components/experiments/results/SetupDisclosure"

const DAY_MS = 24 * 60 * 60 * 1000
const PREVIEW_DEBOUNCE_MS = 400
export const NEEDS_BACKEND = "Needs the latest backend"
export const NOT_COLLECTED = "Only collected when it is the launch goal"

const fmtShortDate = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
const fmtDuration = (seconds: number) => {
  const s = Math.round(seconds)
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`
}
const letterOf = (i: number) => String.fromCharCode(65 + i)

const GOAL_TO_TYPE: Record<string, TestType> = {
  revenue: "revenue",
  page_funnel: "funnel",
  external_page_funnel: "funnel",
  form_submissions: "conversion",
  click_through_rate: "conversion",
  session_time: "engagement",
  bounce_rate: "engagement",
}

export type DetailVariant = ExperimentVariant & { letter: string }

/** goalMetrics sends "" for a null target. */
export const clickTargetName = (t: ClickSetTarget) =>
  elementName({ target: t.target || null, secondary: t.secondary }) || t.secondary || "Unlabelled element"

function sourcesFor(variants: DetailVariant[], metricsData: any, byLetter: Record<string, GoalMetrics | null> | null): VariantSource[] {
  const raw: Record<string, any> = {
    A: metricsData?.urlAMetrics,
    B: metricsData?.urlBMetrics,
    C: metricsData?.urlCMetrics,
    D: metricsData?.urlDMetrics,
  }
  return variants.map((v) => ({
    id: v.id,
    label: v.label,
    letter: v.letter,
    isControl: v.isControl,
    // Exposure is the primary request's per-variant sessions, the same
    // population as the launch conversion rate. goalMetrics' top-level
    // visitors can double-count wildcard variants, so it is never used.
    visitors: v.sessions || 0,
    launchEvents: v.conversions || 0,
    goalMetrics: byLetter?.[v.letter] ?? null,
    topClicks: topClicksFromEvents(raw[v.letter]?.topEvents),
    totalClicks: v.totalClicks ?? null,
    trials: v.trialConversions ?? null,
    payments: v.paymentConversions ?? null,
    revenue: v.totalRevenue ?? null,
  }))
}

function useDebounced<T>(value: T, ms: number): T {
  const [out, setOut] = useState(value)
  useEffect(() => {
    const t = window.setTimeout(() => setOut(value), ms)
    return () => window.clearTimeout(t)
  }, [value, ms])
  return out
}

export function useExperimentDetailModel({
  testSlugId,
  propertyId: pagePropertyId,
  shareToken,
  isShareMode,
  domain,
}: {
  testSlugId?: string
  propertyId?: string
  shareToken?: string
  /** True only under ShareLayout (magic links): no stop, call, ship or goal edits. */
  isShareMode?: boolean
  domain: string
}) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const experimentId = testSlugId ? parseSlugId(testSlugId).id : null
  const { data: experimentsData, isLoading, refetch } = useExperiments({ propertyId: pagePropertyId, token: shareToken })
  const experiment: Experiment | undefined = useMemo(
    () => experimentsData?.find((exp: Experiment) => exp.experimentId === experimentId),
    [experimentsData, experimentId],
  )
  // Actions go to the experiment's own property (aggregate mode lists several).
  const propertyId = experimentPropertyId(experiment, pagePropertyId)
  const isActive = experiment?.status === "active"
  const readOnly = detailReadOnly({ token: shareToken, isShareMode })

  const ctx: GoalContext = useMemo(
    () => ({ launchGoal: experiment?.testGoal ?? "", finishPageUrl: experiment?.finishPageUrl ?? null }),
    [experiment?.testGoal, experiment?.finishPageUrl],
  )

  // Primary: exactly today's request; launch goal, health and chart read it.
  const { data: metricsData, isLoading: metricsLoading } = useExperimentMetrics(experimentId, {
    propertyId,
    includeTimeSeries: true,
    token: shareToken,
    live: isActive,
  })

  // ── The primary (saved) goal ──
  // The goal just saved, until the metrics refetch carries it (no flicker back).
  const [justSaved, setJustSaved] = useState<{ goal: ResultsGoal | null } | null>(null)
  useEffect(() => setJustSaved(null), [metricsData])
  const savedGoal = useMemo(
    () =>
      justSaved
        ? justSaved.goal
        : parseResultsGoal(metricsData?.resultsGoal ?? metricsData?.experiment?.resultsGoal ?? experiment?.resultsGoal),
    [justSaved, metricsData, experiment?.resultsGoal],
  )
  const primary = useMemo(() => effectiveGoal(savedGoal, ctx).goal, [savedGoal, ctx])
  // goals=all counts one destination per request: an unsaved one being shown, else the saved one.
  const [destinationDraft, setDestinationDraft] = useState<string | null>(null)
  const destinationParam =
    destinationDraft ?? (primary?.type === "destination" && !sameGoal(primary, launchResultsGoal(ctx)) ? (primary.destination ?? null) : null)

  // Secondary, opt-in: every goal's numbers (the saved click set is applied
  // server-side). Never blocks the page; a failure or an older backend leaves
  // the launch goal on today's fields.
  const goalQuery = useExperimentGoalMetrics(experimentId, {
    propertyId,
    token: shareToken,
    destination: destinationParam,
    live: isActive,
    enabled: Boolean(experiment),
  })
  const goalMetricsByLetter = goalQuery.data?.status === "ok" ? goalQuery.data.byLetter : null

  // ── Versions ──
  const variants: DetailVariant[] = useMemo(() => {
    if (!experiment) return []
    const isComponentTest = experiment.testType?.startsWith("component_")
    return (experiment.variants || []).map((v, index) => {
      const id = v.id || `variant-${index}`
      return {
        ...v,
        id,
        isControl: v.isControl ?? index === 0,
        label: v.label || (index === 0 ? "Control" : `Variant ${String.fromCharCode(isComponentTest ? 64 + index : 65 + index)}`),
        letter: letterOf(index),
        ...(metricsData?.variants?.[id] ?? {}),
      }
    })
  }, [experiment, metricsData])
  const sources = useMemo(() => sourcesFor(variants, metricsData, goalMetricsByLetter), [variants, metricsData, goalMetricsByLetter])
  const elements = useMemo(() => clickElements(sources), [sources])
  // Feature detection only from goalMetrics v2: an old backend answers the
  // unknown PUT route with 404, which a new one also uses for "not found".
  const supported = supportsResultsGoal(sources)
  const full = sources.length > 0 && sources.every((s) => s.goalMetrics)

  // Key clicks needs its own deduped count unless it is the saved set: one
  // `clickSet=` request, only when 2+ elements match (one element's own
  // unique visitors are exact). A backend without the "key" preset gets the
  // matches as a custom set. Not polled.
  const savedPreset = primary?.type === "clicks" ? primary.clicks?.preset : null
  const supportsKey = full && sources.every((s) => s.goalMetrics?.clickSetPresets.includes("key"))
  const keyMatches = elements.filter((e) => clickSetIncludes(KEY_CLICKS, e))
  const keyQuery = useExperimentGoalMetrics(experimentId, {
    propertyId,
    token: shareToken,
    clickSet: clickSetParam(supportsKey ? KEY_CLICKS : presetToCustom(KEY_CLICKS, keyMatches)),
    enabled: supported && savedPreset !== "key" && keyMatches.length > 1,
  })

  // ── Custom click set draft (popover) ──
  const [editorOpen, setEditorOpen] = useState(false)
  const [clickDraft, setClickDraft] = useState<ClickSet>(KEY_CLICKS)
  /** A custom click set or destination in the list that isn't saved (share links, older backends, or while saving). */
  const [shownDraft, setShownDraft] = useState<ResultsGoal | null>(null)
  const debouncedDraft = useDebounced(clickDraft, PREVIEW_DEBOUNCE_MS)
  const draftQueryParam = clickSetIsValid(debouncedDraft) ? clickSetParam(debouncedDraft) : null
  const draftQuery = useExperimentGoalMetrics(experimentId, {
    propertyId,
    token: shareToken,
    clickSet: draftQueryParam,
    enabled: supported && editorOpen && Boolean(draftQueryParam),
  })
  const shownDraftQuery = useExperimentGoalMetrics(experimentId, {
    propertyId,
    token: shareToken,
    clickSet: shownDraft?.type === "clicks" && shownDraft.clicks ? clickSetParam(shownDraft.clicks) : null,
    enabled: supported && shownDraft?.type === "clicks" && Boolean(shownDraft.clicks),
  })
  const byLetterOf = (q: { data?: { status: string; byLetter?: Record<string, GoalMetrics | null> } }) =>
    q.data?.status === "ok" ? (q.data.byLetter ?? null) : null

  // Count goals (the launch click goal) take the spread from the daily series.
  const dispersion = useMemo(() => {
    if (!isCountGoal(ctx.launchGoal)) return 1
    const series = metricsData?.timeSeries || []
    return estimateCountDispersion(
      variants.map((v) =>
        series.map((day: Record<string, number>) => {
          const exposure = Number(day[`${v.id}_sessions`]) || 0
          return { exposure, events: ((Number(day[`${v.id}_conversionRate`]) || 0) * exposure) / 100 }
        }),
      ),
    )
  }, [ctx.launchGoal, metricsData?.timeSeries, variants])

  // ── The goal list ──
  const stripe = {
    trials: !full || sources.every((s) => s.goalMetrics?.trials) || sources.some((s) => s.trials != null),
    payments: !full || sources.every((s) => s.goalMetrics?.payments) || sources.some((s) => s.payments != null),
  }
  const candidates = useMemo(
    () =>
      goalCandidates({
        ctx,
        primary,
        customSets: primary?.type === "clicks" && primary.clicks?.preset === "custom" ? [primary] : [],
        draft: shownDraft,
        stripe,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stripe is two booleans derived from sources
    [ctx, primary, shownDraft, stripe.trials, stripe.payments],
  )
  const keySources = byLetterOf(keyQuery)
  const shownDraftSources = byLetterOf(shownDraftQuery)
  const evaluated = useMemo(
    () =>
      evaluateGoals({
        candidates,
        ctx,
        dispersion,
        nameOf: clickTargetName,
        unavailableReason: full ? null : goalQuery.isLoading ? "Loading…" : NOT_COLLECTED,
        sourcesFor: (c) => {
          const preset = c.goal?.type === "clicks" ? c.goal.clicks?.preset : null
          const byLetter = c.isDraft && c.goal?.type === "clicks" ? shownDraftSources : preset === "key" && savedPreset !== "key" ? keySources : null
          return byLetter ? sourcesFor(variants, metricsData, byLetter) : sources
        },
      }),
    [candidates, sources, keySources, shownDraftSources, savedPreset, variants, metricsData, ctx, full, goalQuery.isLoading, dispersion],
  )

  const primaryKey = goalKey(primary)
  const selected = selectedCandidate(candidates, searchParams.get("goal"))!
  const { result, comparison } = evaluated.get(selected.key) ?? evaluated.get(primaryKey)!
  const setSelectedKey = useCallback(
    (key: string) =>
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          if (key === primaryKey) p.delete("goal")
          else p.set("goal", key)
          return p
        },
        { replace: true },
      ),
    [primaryKey, setSearchParams],
  )

  const listRows: GoalRowSummary[] = goalRows(evaluated, primaryKey)

  // ── Draft preview for the popover ──
  const preview: GoalPreview | null = useMemo(() => {
    if (!editorOpen) return null
    const previewSources = sourcesFor(variants, metricsData, byLetterOf(draftQuery))
    const r = selectGoalMetric({ type: "clicks", clicks: debouncedDraft }, ctx, previewSources, { isLaunch: false, nameOf: clickTargetName })
    return {
      result: r,
      comparison: compareGoal(r, { dispersion }),
      loading: clickDraft !== debouncedDraft || draftQuery.isFetching,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- byLetterOf reads draftQuery.data
  }, [editorOpen, draftQuery.data, draftQuery.isFetching, variants, metricsData, debouncedDraft, ctx, dispersion, clickDraft])

  // ── Saving the primary goal ──
  const saveMutation = useSaveResultsGoal({ propertyId, token: shareToken })
  const persistGoal = async (next: ResultsGoal | null) => {
    if (!experiment) return
    try {
      await saveMutation.mutateAsync({ experimentId: experiment.experimentId, resultsGoal: resultsGoalBody(next) })
      setJustSaved({ goal: next })
      setEditorOpen(false)
      // The new primary is what the page opens on: drop ?goal=.
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          p.delete("goal")
          return p
        },
        { replace: true },
      )
      setShownDraft(null)
      setDestinationDraft(null)
    } catch (error) {
      const status = getHttpStatus(error)
      const body = getErrorBody(error) as { error?: unknown } | null
      toast.error(
        status === 409
          ? "Someone else changed the goal. Reload to see it."
          : status === 400 && typeof body?.error === "string"
            ? `Couldn't save the goal: ${body.error}`
            : "Failed to save the goal",
      )
    }
  }
  // Making the launch goal primary again is a reset (null).
  const makePrimary = (goal: ResultsGoal | null) => void persistGoal(!goal || sameGoal(goal, launchResultsGoal(ctx)) ? null : goal)
  // Share links can't save (the PUT refuses them); older backends have no PUT,
  // and only newer ones know the "key" preset.
  const canSave = (goal: ResultsGoal | null) =>
    !readOnly && supported && !(goal?.type === "clicks" && goal.clicks?.preset === "key" && !supportsKey)
  /** One interaction: show the goal, and save it as the test's goal when allowed. */
  const chooseGoal = (goal: ResultsGoal | null) => {
    const key = goalKey(goal)
    setSelectedKey(key)
    if (key !== primaryKey && canSave(goal)) makePrimary(goal)
  }

  // ── Timing, readiness, call gates for the selected goal ──
  const startDate = useMemo(() => (experiment ? new Date(experiment.createdAt) : new Date()), [experiment])
  const endDate = useMemo(() => (experiment?.endedAt ? new Date(experiment.endedAt) : null), [experiment?.endedAt])
  const testType = GOAL_TO_TYPE[ctx.launchGoal] || "conversion"
  const maxDays = TEST_TYPE_CONFIGS[testType].maxDuration
  const totalVisitors = variants.reduce((s, v) => s + (v.sessions || 0), 0)
  const daysRunning = Math.max(1, Math.ceil(((endDate ?? new Date()).getTime() - startDate.getTime()) / DAY_MS))
  const dailyVisitors = Math.round(totalVisitors / daysRunning)

  const plan = useMemo(
    () =>
      goalCallPlan({
        result,
        comparison,
        daysRunning,
        dailyVisitors,
        isComponentTest: Boolean(experiment?.testType?.includes("component")),
        dispersion,
      }),
    [result, comparison, daysRunning, dailyVisitors, experiment?.testType, dispersion],
  )
  const { minDays, estimatedTotalDays, earliestCallDay, chance } = plan
  const gateRows = plan.gates

  const health = useMemo(
    () =>
      assessHealth({
        isActive,
        startedAt: startDate,
        endedAt: endDate,
        minDays,
        maxDays,
        autoStopDays: experiment?.autoStopDurationDays ?? null,
        expectedShares:
          experiment?.customDistribution && experiment.customDistribution.length === variants.length
            ? experiment.customDistribution.map((d) => (d > 1 ? d / 100 : d))
            : undefined,
        estimatedTotalDays,
        earliestCallDay,
        versions: variants.map((v, i) => ({
          id: v.id,
          label: v.label,
          visitors: v.sessions || 0,
          views: v.totalViews ?? null,
          // "Goal not tracked" reads the goal on screen when its numbers are in.
          conversions: result.available ? (result.variants[i]?.events ?? 0) : v.conversions || 0,
          // Today's bounces rows (split tests), else goalMetrics (component tests too).
          bounceRate: v.bounceRate ?? goalBounceRate(goalMetricsByLetter?.[v.letter]),
          scrollDepth: v.scrollDepth ?? null,
          timeOnPage: (v.sessions || 0) > 0 ? (v.avgTimeOnPage ?? null) : null,
        })),
      }),
    [isActive, startDate, endDate, minDays, maxDays, experiment?.autoStopDurationDays, experiment?.customDistribution, estimatedTotalDays, earliestCallDay, variants, goalMetricsByLetter, result],
  )

  const settling = outcomeSettling({ isActive, endedAt: experiment?.endedAt })
  const untrusted = health.status === "check"
  const decision = callDecision({
    gates: gateRows,
    descriptive: result.metric === "descriptive" || !result.available,
    settling: result.stripe && settling.settling,
    untrusted,
  })
  const winnerVariant = comparison.winner ? (variants.find((v) => v.id === comparison.winner!.id) ?? null) : null
  const isElementTest = Boolean(experiment?.testType?.startsWith("component") || experiment?.testType === "element")
  // Orange, Call and Ship: chance ≥ 95%, gates met, settled, health not Check.
  const canCall = !readOnly && isActive && decision.callable
  const canShip = canCall && isElementTest && Boolean(winnerVariant)

  // Running tests: can the goal on screen be called, and when at the earliest.
  const callStatus: CallStatus =
    !isActive || !result.available || result.metric === "descriptive" || gateRows.length === 0
      ? null
      : untrusted
        ? "held"
        : decision.callable
          ? "callable"
          : "waiting"
  const callDay = earliestCallDay ?? minDays
  const earliestCall =
    callStatus !== "waiting" ? null : callDay > 365 ? "over a year away" : `~${fmtShortDate(new Date(startDate.getTime() + callDay * DAY_MS))}`

  // ── Engagement: Control against the leading version on the goal shown ──
  const engagementRows: ResultRow[] = useMemo(() => {
    const ctrl = variants.find((v) => v.isControl)
    const lead = variants.find((v) => v.id === comparison.focus?.id) ?? variants.find((v) => !v.isControl)
    if (!ctrl || !lead || !(ctrl.sessions && lead.sessions)) return []
    const out: ResultRow[] = []
    const add = (
      key: string,
      label: string,
      a: number | null | undefined,
      b: number | null | undefined,
      fmt: (x: number) => string,
      lowerIsBetter: boolean,
      stats?: { chance: number; range: { lower: number; upper: number } | null } | null,
    ) => {
      if (a == null || b == null) return
      const change = a > 0 ? ((b - a) / a) * 100 : null
      const same = change == null || Math.abs(change) < 1
      const better = lowerIsBetter ? b < a : b > a
      // Confidence in the direction of the tag: P(lead's rate moved that way).
      const confidence = stats && !same ? (b > a ? stats.chance : 1 - stats.chance) : null
      out.push({
        key,
        label,
        note: lowerIsBetter ? "lower is better" : null,
        available: true,
        rates: `${fmt(a)} → ${fmt(b)}`,
        change: change == null ? null : formatChange(change),
        tag: same ? "same" : better ? "better" : "worse",
        tagStrong: confidence != null && confidence >= 0.8,
        chance: confidence != null ? formatChance(confidence) : null,
        confidence,
        range: stats?.range && change != null ? { ...stats.range, point: change } : null,
      })
    }
    const gc = goalMetricsByLetter?.[ctrl.letter]
    const gl = goalMetricsByLetter?.[lead.letter]
    const bounceStats =
      gc && gl && gc.bounces.visitors > 0 && gl.bounces.visitors > 0
        ? compareProportions(gc.bounces.bounced, gc.bounces.visitors, gl.bounces.bounced, gl.bounces.visitors)
        : null
    add("bounce", "Bounce rate", ctrl.bounceRate ?? goalBounceRate(gc), lead.bounceRate ?? goalBounceRate(gl), (x) => `${x.toFixed(1)}%`, true, bounceStats)
    add("time", "Time on page", ctrl.avgTimeOnPage, lead.avgTimeOnPage, fmtDuration, false)
    add("scroll", "Scroll depth", ctrl.scrollDepth, lead.scrollDepth, (x) => `${Math.round(x)}%`, false)
    return out
  }, [variants, comparison.focus?.id, goalMetricsByLetter])

  // ── Header ──
  const status = experiment?.status
  const statusChip = (() => {
    if (status === "active") return { label: `Running · day ${daysRunning}`, dot: "bg-live" }
    if (status === "completed") return { label: `Completed on day ${daysRunning}`, dot: "bg-muted-foreground" }
    if (status === "paused" && endDate)
      return {
        label: experiment?.stoppedReason === "auto" ? `Auto-stopped on day ${daysRunning}` : `Stopped on day ${daysRunning}`,
        dot: "bg-muted-foreground",
      }
    if (status === "paused") return { label: "Stopped", dot: "bg-muted-foreground" }
    if (status === "archived") return { label: "Archived", dot: "bg-border" }
    return { label: "Draft", dot: "bg-border" }
  })()

  const control = variants.find((v) => v.isControl)
  const pagePath = (() => {
    const url = control?.url || ""
    if (!url) return domain
    try {
      const parsed = new URL(/^https?:\/\//i.test(url) ? url : `https://${domain || "site"}${url.startsWith("/") ? "" : "/"}${url}`)
      return `${parsed.hostname.replace(/^www\./, "")}${parsed.pathname}`
    } catch {
      return url
    }
  })()
  const splitLabel =
    experiment?.customDistribution && experiment.customDistribution.length === variants.length
      ? experiment.customDistribution.map((d) => Math.round(d > 1 ? d : d * 100)).join(" / ")
      : variants.map(() => Math.round(100 / Math.max(variants.length, 1))).join(" / ")
  // ── Setup ──
  const previewLinks = variants
    .filter((v) => v.url)
    .map((v) => ({ label: v.label || v.id, href: resolveVariantUrl(v.url, domain, v.id, experiment) }))
  const setupRows: SetupRow[] = experiment
    ? [
        { label: "Goal at launch", value: formatTestGoalLabel(experiment.testGoal, launchGoalCopy(experiment.testGoal).label) },
        ...(!sameGoal(primary, launchResultsGoal(ctx)) ? [{ label: "Goal", value: goalName(primary, ctx, clickTargetName) }] : []),
        ...(experiment.finishPageUrl ? [{ label: "Destination", value: experiment.finishPageUrl }] : []),
        { label: "Versions", value: variants.map((v) => v.label).join(", "), links: previewLinks },
        { label: "Split", value: `${splitLabel}${experiment.customDistribution ? " · custom" : ""}` },
        { label: "Audience", value: audienceLabel(experiment) },
        { label: "Page", value: pagePath || "—" },
        { label: "Counting", value: experiment.isUserLevel ? "Per person" : "Per visit" },
        { label: "Minimum run", value: `${minDays} days · no call before ${fmtShortDate(new Date(startDate.getTime() + minDays * DAY_MS))}` },
        {
          label: "Maximum run",
          value: experiment.autoStopDurationDays
            ? `${experiment.autoStopDurationDays} days · auto-stops ${fmtShortDate(new Date(startDate.getTime() + experiment.autoStopDurationDays * DAY_MS))}`
            : `${maxDays} days recommended · no auto-stop`,
        },
        { label: "Started", value: startDate.toLocaleString() },
        ...(endDate
          ? [{ label: "Ended", value: `${endDate.toLocaleString()} · ${experiment.stoppedReason === "auto" ? "auto-stopped" : "stopped by hand"}` }]
          : []),
        ...variants.filter((v) => v.notes).map((v) => ({ label: `${v.label} notes`, value: v.notes })),
        { label: "Experiment ID", value: experiment.experimentId, mono: true },
      ]
    : []

  // ── Actions ──
  const stopMutation = useStopExperiment({ propertyId })
  const resumeMutation = useResumeExperiment({ propertyId })
  const archiveMutation = useArchiveExperiment({ propertyId })
  const callMutation = useCallExperiment({ propertyId })
  const shipMutation = useShipWinner({ propertyId })
  const run = async (fn: () => Promise<unknown>, ok: string, fail: string | ((error: unknown) => string), after?: () => void) => {
    try {
      await fn()
      toast.success(ok)
      if (after) after()
      else refetch()
    } catch (error) {
      toast.error(typeof fail === "function" ? fail(error) : fail)
    }
  }
  const lifecycle = (action: LifecycleAction) => (error: unknown) => lifecycleErrorMessage(action, error)
  const lift = comparison.winner?.change ?? null
  const decisionLog = () => ({
    experimentName: experiment!.experimentName,
    testGoal: experiment!.testGoal,
    testType: experiment!.testType,
    pageUrl: control?.url,
    winnerLabel: winnerVariant?.label || winnerVariant?.id,
    domain,
  })
  const actions = {
    stop: () => experiment && run(() => stopMutation.mutateAsync(experiment.experimentId), "Experiment stopped", lifecycle("stop")),
    resume: () => experiment && run(() => resumeMutation.mutateAsync(experiment.experimentId), "Experiment resumed", lifecycle("resume")),
    archive: () =>
      experiment &&
      run(() => archiveMutation.mutateAsync(experiment.experimentId), "Experiment archived", lifecycle("archive"), () => navigate(-1)),
    selectGoal: (key: string) => {
      const c = candidates.find((x) => x.key === key)
      if (c) chooseGoal(c.goal)
    },
    /** The Key clicks pencil: start from Key clicks (or the saved click set when it is one). */
    editKeyClicks: () => {
      setClickDraft(primary?.type === "clicks" && primary.clicks && primary.clicks.preset !== "all" ? primary.clicks : KEY_CLICKS)
      setEditorOpen(true)
    },
    /** "+ Add goal" → Clicks on elements: start from an empty custom set. */
    addClicks: () => {
      setClickDraft({ preset: "custom", targets: [] })
      setEditorOpen(true)
    },
    closeClickEditor: (open: boolean) => {
      if (!open) setEditorOpen(false)
    },
    setClickDraft,
    useClickDraft: () => {
      const goal: ResultsGoal = { type: "clicks", clicks: clickDraft }
      if (clickDraft.preset === "custom") setShownDraft(goal)
      setEditorOpen(false)
      chooseGoal(goal)
    },
    addDestination: (raw: string, kind: "page" | "link") => {
      const destination = destinationInput(raw, kind)
      if (!destination) return
      const goal: ResultsGoal = { type: "destination", destination }
      setShownDraft(goal)
      setDestinationDraft(destination)
      chooseGoal(goal)
    },
    call: async () => {
      if (!experiment || !decision.callable) return
      const leader = comparison.focus
      const interpretation = {
        recommendation: winnerVariant
          ? `${winnerVariant.label} beats Control${leader?.change != null ? ` by ${formatChange(leader.change)}` : ""}.`
          : "No version beats Control.",
      }
      await run(
        () =>
          callMutation.mutateAsync({
            decision: {
              experimentId: experiment.experimentId,
              winnerVariantId: winnerVariant?.id ?? null,
              action: winnerVariant ? "stop" : "no_ship",
              liftPercent: lift,
              // The decision log keeps its field name; it now carries the Bayesian chance.
              significancePercent: Math.round(chance * 1000) / 10,
              rationale: `${interpretation.recommendation} Goal: ${result.label}, ${formatChance(chance)} chance to beat Control.`,
            },
            log: decisionLog(),
          }),
        winnerVariant ? `Called the test: ${winnerVariant.label} wins` : "Called the test: no clear winner",
        "Failed to call test",
      )
    },
    ship: async () => {
      if (!experiment || !winnerVariant) return
      try {
        const res = await shipMutation.mutateAsync({
          experimentId: experiment.experimentId,
          winnerVariantId: winnerVariant.id,
          liftPercent: lift,
          significancePercent: Math.round(chance * 1000) / 10,
          log: decisionLog(),
        })
        toast.success(
          res && "pendingBackend" in res && res.pendingBackend
            ? `Winner recorded. ${winnerVariant.label} goes to everyone once the promote endpoint is live.`
            : `${winnerVariant.label} now shows to everyone`,
        )
        refetch()
      } catch {
        toast.error("Failed to apply the winner")
      }
    },
    exportCsv: () => {
      if (!experiment) return
      const csv = buildResultsCsv({
        name: experiment.experimentName,
        goalLabel: formatTestGoalLabel(experiment.testGoal, "Page View"),
        status: experiment.status,
        createdAt: experiment.createdAt,
        days: daysRunning,
        goal: { result, comparison },
        totalViews: variants.map((v) => ({ label: v.label, views: v.totalViews ?? null })),
        urls: variants.map((v) => ({ label: v.label, url: v.url || "" })),
      })
      downloadCsv(`${experiment.experimentName.replace(/[^a-z0-9]/gi, "_").toLowerCase()}_results.csv`, csv)
      toast.success("Experiment data exported to CSV")
    },
  }

  return {
    isLoading,
    metricsLoading: Boolean(experiment) && metricsLoading && !metricsData,
    experiment,
    variants,
    readOnly,
    isActive,
    isPaused: status === "paused",
    statusChip,
    goal: {
      rows: listRows,
      selectedKey: selected.key,
      isPrimary: selected.key === primaryKey,
      keyClicksKey: goalKey({ type: "clicks", clicks: KEY_CLICKS }),
      result,
      comparison,
      settling,
      supported,
      // Share (magic-link) viewers only switch the view; the PUT refuses them.
      editDisabledReason: supported ? null : NEEDS_BACKEND,
      stripeConnected: stripe.trials || stripe.payments,
      elements,
      saving: saveMutation.isPending,
    },
    clickEditor: { open: editorOpen, draft: clickDraft, preview },
    decision: {
      winner: winnerVariant,
      chance,
      callable: decision.callable,
      canCall,
      canShip,
      untrusted,
    },
    call: { status: callStatus, earliestCall, gates: gateRows },
    engagement: engagementRows,
    header: {
      visitors: totalVisitors,
      split: health.split,
      enoughData: isActive && callStatus != null ? (callStatus === "waiting" ? earliestCall : "enough data") : null,
      pagePath,
    },
    health,
    timeSeries: metricsData?.timeSeries ?? null,
    dispersion,
    setupRows,
    pending: {
      stop: stopMutation.isPending,
      resume: resumeMutation.isPending,
      call: callMutation.isPending,
      ship: shipMutation.isPending,
    },
    actions,
  }
}

export type ExperimentDetailModel = ReturnType<typeof useExperimentDetailModel>

function goalBounceRate(g: GoalMetrics | null | undefined): number | null {
  return g && g.bounces.visitors > 0 ? (g.bounces.bounced / g.bounces.visitors) * 100 : null
}

function resolveVariantUrl(url: string, domain: string, variantId: string, experiment?: Experiment): string {
  if (!url || /^https?:\/\//i.test(url) || !domain) return url
  const base = domain.startsWith("http") ? domain : `https://${domain}`
  const parsed = new URL(`${base.replace(/\/$/, "")}${url.startsWith("/") ? "" : "/"}${url}`)
  if (experiment?.testType?.startsWith("component_")) {
    parsed.searchParams.set("hmbl_variant", variantId)
    parsed.searchParams.set("hmbl_exp", experiment.experimentId)
  } else {
    parsed.searchParams.set("_st", "y")
  }
  return parsed.toString()
}

/** Audience restrictions spelled out (devices, traffic sources, countries). */
function audienceLabel(experiment: Experiment): string {
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
  const parts: string[] = []
  const devices = experiment.audienceRestriction?.devices || []
  const channels = experiment.audienceRestriction?.channels || []
  const countries = experiment.geoRestriction?.countries || []
  if (devices.length) parts.push(devices.map(cap).join(", "))
  if (channels.length) parts.push(channels.map(cap).join(", "))
  if (countries.length) parts.push(countries.map((c) => countryCodes[c] || c).join(", "))
  return parts.length ? parts.join(" · ") : "All countries · all devices"
}
